import DOMPurify from "dompurify";
import * as React from "react";

import styles from "./KnowledgeBaseView.scss";
import LeftArrow from "components/icons/LeftArrow";
import { KnowledgeBaseArticle, knowledgeBaseService } from "services/news/KnowledgeBaseService";
import { logger } from "utils/logging";
import { useTheme } from "utils/useTheme";

interface Props {
    selectedPage: KnowledgeBaseArticle;
    selectedLanguage: string;
    onBackClick: () => void;
    onNavigate: (page: KnowledgeBaseArticle) => void;
    onExpandToShowPage: (page: KnowledgeBaseArticle) => Promise<void>;
}

const componentPurify = DOMPurify(window);

componentPurify.addHook("afterSanitizeAttributes", (node) => {
    if (node instanceof Element && node.getAttribute("data-macro-name") === "ui-button") {
        node.removeAttribute("title");
    }
});

export const KnowledgeBasePageContent: React.FC<Props> = ({
    selectedPage,
    selectedLanguage,
    onBackClick,
    onNavigate,
    onExpandToShowPage,
}) => {
    const theme = useTheme();
    const scrollToAnchor = (anchor: string) => {
        const normalized = anchor.replace(/-/g, "").toLowerCase();
        const element = Array.from(document.querySelectorAll("[id]")).find((el) =>
            el.id.replace(/-/g, "").toLowerCase().endsWith(normalized)
        );
        if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    const handleContentClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const target = e.target as HTMLElement;
        const link = target.closest("a");
        if (link) {
            e.preventDefault();
            const pageId = link.getAttribute("data-linked-resource-id");
            const href = link.getAttribute("href");

            if (pageId && pageId !== "null" && link.getAttribute("data-linked-resource-type") === "page") {
                // Internal KB link with data attributes
                knowledgeBaseService
                    .fetchPageById(selectedLanguage, pageId)
                    .then(async (page) => {
                        onNavigate(page);
                        await onExpandToShowPage(page);
                    })
                    .catch(logger.error);
            } else if (href && href.includes("/wiki/spaces/KB/pages/")) {
                // Internal KB link with wiki URL format
                const match = href.match(/\/wiki\/spaces\/KB\/pages\/(\d+)/);
                const anchorMatch = href.match(/#(.+)$/);

                if (match) {
                    const extractedPageId = match[1];
                    const anchor = anchorMatch ? anchorMatch[1] : null;

                    if (extractedPageId === selectedPage.id && anchor) {
                        scrollToAnchor(anchor);
                    } else {
                        knowledgeBaseService
                            .fetchPageById(selectedLanguage, extractedPageId)
                            .then(async (page) => {
                                if (page) {
                                    onNavigate(page);
                                    await onExpandToShowPage(page);
                                    if (anchor) {
                                        setTimeout(() => scrollToAnchor(anchor), 100);
                                    }
                                } else {
                                    logger.error(`Page not found: ${extractedPageId}`);
                                }
                            })
                            .catch(logger.error);
                    }
                }
            } else if (href) {
                window.open(href, "_blank", "noopener,noreferrer");
            }
        }
    };

    return (
        <div className={styles.pageContent}>
            <button onClick={onBackClick} className={styles.backButton}>
                <LeftArrow color={theme.iconFillColor} />
            </button>
            <h1 className={styles.pageTitleMain}>{selectedPage.title}</h1>
            <div
                className={styles.pageContent}
                dangerouslySetInnerHTML={{ __html: componentPurify.sanitize(selectedPage.body || "") }}
                onClick={handleContentClick}
            />
        </div>
    );
};
